import type { SearchKey } from './types'

/** Listing search keys forwarded listing → detail → wizard (and back). */
export const LISTING_SEARCH_KEYS = [
  'q',
  'city',
  'country',
  'type',
  'available_from',
  'available_to',
  'flex',
  'guests',
  'min_bedrooms',
  'min_bathrooms',
  'min_price',
  'max_price',
  'amenities',
  'accessibility',
  'cancellation',
  'has_availability',
  'only_active',
  'sort',
  'page',
] as const satisfies readonly SearchKey[]

export function readString(search: Record<string, unknown>, key: SearchKey): string {
  return typeof search[key] === 'string' ? (search[key] as string) : ''
}

export function readCsv(search: Record<string, unknown>, key: SearchKey): string[] {
  return readString(search, key)
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
}

export function toggleValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
}

/**
 * Keeps known listing (and optional booking) search params for handoff links.
 * Returns `undefined` when empty so TanStack `Link` can omit `search`.
 */
export function pickListingSearch(
  search: Record<string, unknown>,
  extraKeys: readonly string[] = [],
): Record<string, unknown> | undefined {
  const allowed = new Set<string>([...LISTING_SEARCH_KEYS, ...extraKeys])
  const next: Record<string, unknown> = {}
  for (const key of allowed) {
    const value = search[key]
    if (value === undefined || value === null || value === '') continue
    next[key] = value
  }
  return Object.keys(next).length > 0 ? next : undefined
}

/** Permissive `validateSearch` for reservation listing / property shell routes. */
export function validateListingSearch(search: Record<string, unknown>): Record<string, unknown> {
  return pickListingSearch(search, ['slot', 'reservation_id']) ?? {}
}
